//* Libraries imports
import { motion } from "framer-motion";
import { Card, Table, Badge, Button } from "react-bootstrap";

//* Icons imports
import {
  BsFileEarmarkPdfFill,
  BsDownload,
  BsFiletypePdf,
  BsFiletypeDoc,
  BsFiletypeJpg,
} from "react-icons/bs";
import Icon from "../../../../components/common/Icon";

//* Styles imports
import "./enrollment-documents.scss";

//* Types
import { Enrollment } from "../../../../schemas/common/enrollment/enrollment";

//* Constants
import { STORAGE_URL } from "../../../../constants/consts";

type EnrollmentDocumentsProps = {
  enrollment: Enrollment;
};

export function EnrollmentDocuments({ enrollment }: EnrollmentDocumentsProps) {
  const getDocumentLabel = (type: string) => {
    switch (type) {
      case "cpf":
        return "CPF";
      case "rg_frente":
        return "RG - Frente";
      case "rg_verso":
        return "RG - Verso";
      case "certidao_nascimento_ou_casamento":
        return "Certidão de Nascimento ou Casamento";
      case "historico_escolar":
        return "Histórico Escolar";
      case "comprovante_residencia":
        return "Comprovante de Residência";
      case "declaracao_instituicao_superior":
        return "Declaração de Instituição Superior";
      case "comprovante_aprovacao":
        return "Comprovante de Aprovação";
      case "comprovante_vestibular":
        return "Comprovante de Vestibular";
      case "laudo_medico":
        return "Laudo Médico";
      case "foto_3x4":
        return "Foto 3x4";
      default:
        return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return BsFiletypePdf;
      case "jpg":
      case "jpeg":
      case "png":
        return BsFiletypeJpg;
      default:
        return BsFileEarmarkPdfFill;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleDownload = (filePath: string, fileName: string) => {
    const baseUrl = STORAGE_URL?.replace(/\/$/, "") || "";
    const path = filePath.replace(/^\//, "");
    const fullUrl = `${path}`;
    window.open(fullUrl, "_blank");
  };

  const hasDocuments =
    enrollment.documentos && enrollment.documentos.length > 0;
  const hasExames =
    enrollment.examesAnteriores && enrollment.examesAnteriores.length > 0;

  return (
    <motion.div
      className="enrollment-documents-wrapper"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <Card className="documents-card shadow-sm border-0 mb-4">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            <Icon icon={BsFileEarmarkPdfFill} size={20} />
            Documentos Enviados
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {hasDocuments ? (
            <Table responsive hover className="mb-0 documents-table">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "50px" }}></th>
                  <th>Tipo de Documento</th>
                  <th>Nome do Arquivo</th>
                  <th style={{ width: "120px" }} className="text-center">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {enrollment.documentos.map((doc, index) => (
                  <motion.tr
                    key={doc.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.3 }}
                  >
                    <td className="text-center">
                      <Icon
                        icon={getFileIcon(doc.arquivoNomeOriginal)}
                        size={24}
                        className="text-primary"
                      />
                    </td>
                    <td>
                      <Badge bg="secondary" className="px-3 py-2">
                        {getDocumentLabel(doc.tipoDocumento)}
                      </Badge>
                    </td>
                    <td>
                      <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: "300px" }}
                      >
                        {doc.arquivoNomeOriginal}
                      </span>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          handleDownload(
                            doc.arquivoCaminho,
                            doc.arquivoNomeOriginal
                          )
                        }
                        className="d-flex align-items-center gap-1 mx-auto"
                      >
                        <Icon icon={BsDownload} size={14} />
                        <span>Baixar</span>
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="p-4 text-center text-muted">
              <Icon
                icon={BsFileEarmarkPdfFill}
                size={48}
                className="mb-3 opacity-50"
              />
              <p className="mb-0">
                Nenhum documento foi enviado para esta inscrição.
              </p>
            </div>
          )}
        </Card.Body>
      </Card>

      {hasExames && (
        <Card className="documents-card shadow-sm border-0 mb-4">
          <Card.Header className="bg-info text-white">
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <Icon icon={BsFileEarmarkPdfFill} size={20} />
              Exames Anteriores
            </h5>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 documents-table">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "50px" }}></th>
                  <th>Nome do Exame</th>
                  <th>Ano(s)</th>
                  <th>Arquivo</th>
                  <th>Tamanho</th>
                  <th style={{ width: "120px" }} className="text-center">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {enrollment.examesAnteriores?.map((exame, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.3 }}
                  >
                    <td className="text-center">
                      <Icon
                        icon={getFileIcon(exame.exame_documento_nome_original)}
                        size={24}
                        className="text-info"
                      />
                    </td>
                    <td>
                      <strong>{exame.exame_nome}</strong>
                    </td>
                    <td>
                      <Badge bg="info" className="px-3 py-2">
                        {exame.exame_anos}
                      </Badge>
                    </td>
                    <td>
                      <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: "200px" }}
                      >
                        {exame.exame_documento_nome_original}
                      </span>
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatFileSize(exame.exame_documento_tamanho)}
                      </small>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() =>
                          handleDownload(
                            exame.exame_documento_caminho,
                            exame.exame_documento_nome_original
                          )
                        }
                        className="d-flex align-items-center gap-1 mx-auto"
                      >
                        <Icon icon={BsDownload} size={14} />
                        <span>Baixar</span>
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </motion.div>
  );
}
